import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { api, type AuthoredCourse } from '../../api.ts';
import { slugify } from '../../lib/slug.ts';

export function AdminCoursesPage(): JSX.Element {
  const [courses, setCourses] = useState<AuthoredCourse[] | null>(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.authoring
      .courses()
      .then(setCourses)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить курсы'),
      );
  }, []);

  const effectiveSlug = slugTouched ? slug : slugify(title);

  async function create(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError('');

    try {
      const created = await api.authoring.createCourse({
        title: title.trim(),
        slug: effectiveSlug.trim(),
      });
      setCourses([...(courses ?? []), created]);
      setTitle('');
      setSlug('');
      setSlugTouched(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось создать курс');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Курсы</h1>
        <p className="muted">
          Курс виден ученику только после публикации и только если он в него зачислен.
        </p>
      </div>

      {error ? <p className="error">{error}</p> : null}

      <form className="card stack" onSubmit={(e) => void create(e)} style={{ marginBottom: 28 }}>
        <strong>Новый курс</strong>
        <div className="formrow">
          <div className="field" style={{ flex: 2 }}>
            <label htmlFor="new-title">Название</label>
            <input
              id="new-title"
              className="input"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Основы Python"
            />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="new-slug">Адрес</label>
            <input
              id="new-slug"
              className="input"
              value={effectiveSlug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              placeholder="python-basics"
            />
          </div>
        </div>
        <div>
          <button className="btn" type="submit" disabled={busy || title.trim().length === 0}>
            {busy ? 'Создаём…' : 'Создать курс'}
          </button>
        </div>
      </form>

      {courses === null && !error ? <p className="muted">Загружаем…</p> : null}
      {courses?.length === 0 ? (
        <div className="notice">Пока ни одного курса. Создайте первый — форма выше.</div>
      ) : null}

      <div className="stack">
        {(courses ?? []).map((course) => (
          <Link key={course.id} to={`/admin/courses/${course.id}`} className="rowcard">
            <span className="rowcard__main">
              <strong>{course.title}</strong>
              <span className="muted">/{course.slug}</span>
            </span>
            <span className={`badge${course.isPublished ? ' badge--live' : ''}`}>
              {course.isPublished ? 'опубликован' : 'черновик'}
            </span>
          </Link>
        ))}
      </div>
    </>
  );
}
